import { ChangeDetectionStrategy, Component, OnInit, inject, DestroyRef, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map, take } from 'rxjs/operators';

import { MessageService } from '@shared/services/message.service';
import {
  DatabaseNotificationType,
  NotificationsService,
  NotificationStateService,
  AudioService,
} from '@shared/services/notifications';
import { NotificationState } from '@shared/models/notification-state.model';
import { AssignedPatientsService } from '@app/modules/therapist/assigned-patients/assigned-patients.service';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatBadge } from '@angular/material/badge';
import { PrimaryNotificationComponent } from './primary-notification/primary-notification.component';
import { ChatRequestNotificationDirective } from './chat-request-notification.directive';
import { LeadStatusChangeNotificationDirective } from './lead-status-change-notification.directive';
import { AssignedPatientNotificationDirective } from './assigned-patient-notification.directive';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-primary-notification-button',
  templateUrl: './primary-notification-button.component.html',
  styleUrl: './primary-notification-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatBadge,
    MatMenu,
    MatMenuItem,
    PrimaryNotificationComponent,
    ChatRequestNotificationDirective,
    LeadStatusChangeNotificationDirective,
    AssignedPatientNotificationDirective,
    MatDivider,
  ],
})
export class PrimaryNotificationButtonComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private notificationsService = inject(NotificationsService);
  private notificationStateService = inject(NotificationStateService);
  private assignedPatientsService = inject(AssignedPatientsService);
  private audioService = inject(AudioService);
  private messageService = inject(MessageService);

  public readonly notifications = signal<DatabaseNotificationType[]>([]);
  public readonly unreadNotificationsCount = computed(
    () => this.notifications().filter((notification) => notification.read === null).length
  );

  ngOnInit() {
    const notificationObservable = this.notificationsService
      .getNotificationObservable()
      .pipe(takeUntilDestroyed(this.destroyRef));

    notificationObservable
      .pipe(
        map((notifications) => {
          return (
            notifications
              // The following filter removes all notifications pertaining to the booking system.
              // If we want to handle these notifications in the future, then we must ensure two way binding between
              // the dashboard and booking system so clearing the notification in the booking system also clears the
              // notification in the dashboard.
              .filter((notification) => notification['tag'] !== 'booking-system')
              .filter(
                (notification) => notification.data['pubNub'] === 'false' || !Object.hasOwn(notification.data, 'pubNub')
              )
          );
        })
      )
      .subscribe((databaseNotifications) => {
        this.notifications.set(databaseNotifications);

        const unreadAssignedPatients = this.notifications().filter(
          (notification) => notification.data.type === 'assignedPatient' && notification.read === null
        );
        if (unreadAssignedPatients.length > 0) {
          this.assignedPatientsService.dataStateValid$.next(false);
        }

        if (localStorage.getItem('soundNotificationsEnabled') === 'false' ? false : true) {
          const unreadChatRequests = this.notifications().filter(
            (notification) => notification.data.type === 'chatRequest' && notification.read === null
          );

          let alreadyNotified = [];
          this.notificationStateService.notifiedUserRequests.pipe(take(1)).subscribe((n) => (alreadyNotified = n));

          if (unreadChatRequests.length > 0) {
            let unreadRequestArray = this.unreadRequests(unreadChatRequests);

            if (unreadRequestArray.length > 0 && alreadyNotified.length === 0) {
              void this.audioService.playAudioChatRequest();
              unreadRequestArray = this.notificationStateService.setAllUserNotified(unreadRequestArray);
            } else {
              for (const unreadRequest of unreadRequestArray) {
                const notified = alreadyNotified.filter((n) => n.id === unreadRequest.id);

                if (notified.length === 0 && unreadRequest.unread === 1 && !unreadRequest.userNotified) {
                  void this.audioService.playAudioChatRequest();
                  unreadRequestArray = this.notificationStateService.setUserNotified(
                    unreadRequest.id,
                    unreadRequestArray
                  );
                }
              }
            }

            this.notificationStateService.notifiedUserRequests.next(unreadRequestArray);
          }
        }
      });
  }

  public async markAllChatRequestNotificationsAsRead(): Promise<void> {
    const unreadNotifications = this.notifications().filter((notification) => notification.read === null);
    const unreadNotificationIds = unreadNotifications.map((notification) => notification.id);
    try {
      await this.notificationStateService.markAllChatRequestNotificationsAsRead(unreadNotificationIds);
    } catch {
      this.messageService.showMessage('Error: One or more notifications could not be cleared');
    }
  }

  private unreadRequests(unreadChatRequests: DatabaseNotificationType[]): NotificationState[] {
    const addToNotified: NotificationState[] = [];

    for (const unreadRequest of unreadChatRequests) {
      const unread = {
        id: unreadRequest.id,
        unread: 1,
        userNotified: false,
      } as NotificationState;

      addToNotified.push(unread);
    }

    return addToNotified;
  }
}
